import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  AlertOctagon, Loader2, RefreshCw, CheckCircle2, UserPlus, Eye, Search,
  ShieldCheck, FileClock, Radar, RotateCcw, Send, ChevronDown, ChevronRight, Mail, GitBranch,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  SEV_STYLE, INCIDENT_STATUS_STYLE, INCIDENT_STATUS_LABEL, fmtNum, timeAgo, sid,
} from '@/lib/sentinelConstants';
import ProcessHelp from '@/components/ProcessHelp';

const TRANSITIONS = {
  open: ['acknowledged', 'assigned', 'resolved'],
  acknowledged: ['assigned', 'resolved'],
  assigned: ['acknowledged', 'resolved'],
  resolved: ['recovery_verified', 'open'],
  recovery_verified: ['open'],
};
const ACTION_META = {
  acknowledged: { label: 'Acknowledge', icon: Eye },
  assigned: { label: 'Assign to me', icon: UserPlus },
  resolved: { label: 'Resolve', icon: CheckCircle2 },
  recovery_verified: { label: 'Verify recovery', icon: ShieldCheck },
  open: { label: 'Reopen', icon: RotateCcw },
};

function EvidenceRow({ k, v }) {
  return (
    <div className="flex justify-between gap-4 py-1 border-b border-border/40 last:border-0">
      <span className="text-xs text-muted-foreground">{k}</span>
      <span className="text-xs font-medium text-right">{v}</span>
    </div>
  );
}

function IncidentDetail({ incident, onChanged }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState('');
  const [note, setNote] = useState('');
  const [story, setStory] = useState(null);
  const [storyOpen, setStoryOpen] = useState(false);
  const [storyLoading, setStoryLoading] = useState(false);
  const ev = incident.evidence || {};
  const repro = incident.reproducibility || {};

  const toggleStory = async () => {
    const next = !storyOpen;
    setStoryOpen(next);
    if (next && story === null) {
      setStoryLoading(true);
      try { setStory(await api.getSentinelIncidentStory(incident.incident_id)); }
      catch (e) { toast.error(e.message || 'Could not load the story'); setStory({ steps: [] }); }
      finally { setStoryLoading(false); }
    }
  };
  const STORY_META = {
    incident: { label: 'Incident', Icon: AlertOctagon, cls: 'text-rose-500' },
    investigation: { label: 'Investigation', Icon: Search, cls: 'text-sky-500' },
    recommendation: { label: 'Recommendation', Icon: Radar, cls: 'text-violet-500' },
    approval: { label: 'Approval', Icon: ShieldCheck, cls: 'text-emerald-500' },
  };

  const doTransition = async (to) => {
    setBusy(to);
    try {
      await api.transitionSentinelIncident(incident.incident_id, to, note);
      toast.success(`Incident ${INCIDENT_STATUS_LABEL[to] || to}`);
      setNote('');
      onChanged();
    } catch (e) {
      toast.error(e.message || 'Action failed');
    } finally {
      setBusy('');
    }
  };

  const investigate = async () => {
    setBusy('investigate');
    try {
      const r = await api.investigateSentinelIncident(incident.incident_id);
      toast.success('Root-cause investigation started');
      onChanged();
      return r;
    } catch (e) {
      toast.error(e.message || 'Could not start investigation');
    } finally {
      setBusy('');
    }
  };

  const allowed = TRANSITIONS[incident.status] || [];

  return (
    <div className="rounded-xl border border-border/60 bg-card/60 p-4 space-y-4" data-testid={`incident-detail-${incident.incident_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Badge className={SEV_STYLE[incident.severity] || SEV_STYLE.low}>{incident.severity}</Badge>
            <Badge className={INCIDENT_STATUS_STYLE[incident.status]}>{INCIDENT_STATUS_LABEL[incident.status] || incident.status}</Badge>
            <span className="text-xs text-muted-foreground">seen ×{incident.occurrences || 1}</span>
          </div>
          <div className="font-medium mt-1">{incident.headline}</div>
          <div className="text-xs text-muted-foreground">{incident.sentinel_name} · {incident.metric_label || incident.metric}</div>
        </div>
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Evidence</div>
        <div className="rounded-lg bg-muted/30 p-3">
          <EvidenceRow k="What changed" v={ev.what_changed || '—'} />
          <EvidenceRow k="Detection" v={ev.detection_type || '—'} />
          <EvidenceRow k="Observed" v={fmtNum(ev.observed)} />
          <EvidenceRow k="Expected / threshold" v={fmtNum(ev.expected)} />
          <EvidenceRow k="Magnitude" v={`${fmtNum(ev.magnitude_abs)} (${fmtNum(ev.magnitude_pct)}%)`} />
          <EvidenceRow k="Confidence" v={ev.confidence || 'n/a'} />
          <EvidenceRow k="Materiality" v={ev.materiality_reason ? `${ev.materiality_reason} (×${fmtNum(ev.materiality_ratio)})` : '—'} />
          {ev.data_quality ? <EvidenceRow k="Data quality" v={`${ev.data_quality.state} (through ${ev.data_quality.data_through || '—'})`} /> : null}
        </div>
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1 flex items-center gap-1">
          <FileClock className="w-3 h-3" /> Reproducibility
        </div>
        <div className="rounded-lg bg-muted/30 p-3">
          <EvidenceRow k="Engine" v={repro.engine || '—'} />
          <EvidenceRow k="Sentinel version" v={`v${repro.sentinel_version ?? '—'}`} />
          <EvidenceRow k="Model version" v={repro.model_version || '—'} />
          <EvidenceRow k="Data through" v={repro.data_through || '—'} />
          <EvidenceRow k="Forecast model" v={repro.forecast_model || 'n/a'} />
          <EvidenceRow k="Generated at" v={repro.generated_at ? new Date(repro.generated_at).toLocaleString() : '—'} />
        </div>
      </div>

      <Textarea placeholder="Optional note for the audit trail…" value={note} onChange={(e) => setNote(e.target.value)}
        className="text-sm" rows={2} data-testid="incident-note" />

      <div className="flex flex-wrap gap-2">
        {allowed.map((to) => {
          const M = ACTION_META[to] || { label: to, icon: CheckCircle2 };
          const Icon = M.icon;
          return (
            <Button key={to} size="sm" variant={to === 'resolved' || to === 'recovery_verified' ? 'default' : 'outline'}
              disabled={!!busy} onClick={() => doTransition(to)} data-testid={`incident-action-${to}`}>
              {busy === to ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Icon className="w-3.5 h-3.5 mr-1" /> {M.label}</>}
            </Button>
          );
        })}
        {incident.investigation_id ? (
          <Badge variant="outline" className="gap-1"><Search className="w-3 h-3" /> Investigating</Badge>
        ) : (
          <Button size="sm" variant="outline" disabled={!!busy} onClick={investigate} data-testid="incident-investigate">
            {busy === 'investigate' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Search className="w-3.5 h-3.5 mr-1" /> Investigate why</>}
          </Button>
        )}
      </div>

      <div className="border-t border-border/40 pt-3">
        <button type="button" onClick={toggleStory} className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground hover:text-foreground transition-colors" data-testid={`incident-story-toggle-${incident.incident_id}`}>
          {storyOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />} Follow the story
        </button>
        {storyOpen && (
          <div className="mt-2" data-testid={`incident-story-${incident.incident_id}`}>
            {storyLoading ? (
              <div className="flex items-center gap-2 text-xs text-muted-foreground py-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Tracing the thread…</div>
            ) : (story && story.steps && story.steps.length > 1) ? (
              <ol className="relative border-l border-border/50 ml-1.5 space-y-2.5">
                {story.steps.map((s, i) => {
                  const M = STORY_META[s.step] || STORY_META.incident;
                  const Icon = M.Icon;
                  const isAnchor = s.step === 'incident';
                  return (
                    <li key={`${s.step}-${s.id}-${i}`} className="ml-4" data-testid={`story-step-${s.step}-${s.id}`}>
                      <span className={`absolute -left-[7px] w-3.5 h-3.5 rounded-full bg-background border border-border flex items-center justify-center`}><Icon className={`w-2.5 h-2.5 ${M.cls}`} /></span>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                            <span>{M.label}{s.status ? ` · ${s.status}` : ''}</span>
                            {s.at && <span className="text-muted-foreground/70 normal-case tracking-normal">· {new Date(s.at).toLocaleDateString()}</span>}
                          </div>
                          <div className="text-xs text-foreground truncate" title={s.label}>{s.label || '—'}</div>
                        </div>
                        {!isAnchor && s.route && (
                          <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px] shrink-0" onClick={() => navigate(s.route)} data-testid={`story-open-${s.step}-${s.id}`}>
                            Open <ChevronRight className="w-3 h-3 ml-0.5" />
                          </Button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
            ) : (
              <p className="text-xs text-muted-foreground py-2" data-testid={`incident-story-empty-${incident.incident_id}`}>No linked investigation, recommendation or approval yet. Investigate or route a recommendation to build the thread.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function RecoveryDigestPanel() {
  const [open, setOpen] = useState(false);
  const [digest, setDigest] = useState(null);
  const [cadence, setCadence] = useState('off');
  const [busy, setBusy] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [recipients, setRecipients] = useState([]);

  const load = useCallback(async () => {
    try {
      const [d, s, c] = await Promise.all([api.getRecoveryDigest(7), api.getRecoveryDigestSettings(), api.getRecoveryDigestCandidates()]);
      setDigest(d); setCadence(s.cadence || 'off'); setRecipients(s.recipients || []); setCandidates(c.candidates || []);
    } catch { /* noop */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const changeCadence = async (v) => {
    setCadence(v); setBusy('cadence');
    try { await api.setRecoveryDigestSettings(v); toast.success(v === 'weekly' ? 'Weekly recovery digest turned on' : 'Recovery digest turned off'); }
    catch (e) { toast.error(e.message || 'Could not update cadence'); } finally { setBusy(''); }
  };
  const toggleRecipient = async (uid) => {
    const next = recipients.includes(uid) ? recipients.filter((x) => x !== uid) : [...recipients, uid];
    setRecipients(next); setBusy('recips');
    try { await api.setRecoveryDigestSettings(cadence, next); }
    catch (e) { toast.error(e.message || 'Could not update recipients'); setRecipients(recipients); } finally { setBusy(''); }
  };
  const sendNow = async () => {
    setBusy('send');
    try { const r = await api.sendRecoveryDigest(); toast.success(`Digest sent to ${r.sent_inapp} recipient(s)`); load(); }
    catch (e) { toast.error(e.message || 'Send failed'); } finally { setBusy(''); }
  };
  const sendTest = async () => {
    setBusy('test');
    try { await api.testRecoveryDigest(); toast.success('Test digest sent to you — check your inbox'); }
    catch (e) { toast.error(e.message || 'Test failed'); } finally { setBusy(''); }
  };

  const d = digest;
  return (
    <div className="rounded-xl border border-border/60 bg-card/40" data-testid="recovery-digest-panel">
      <div className="flex items-center gap-2 p-3 flex-wrap">
        <button className="flex items-center gap-2 text-sm font-medium" onClick={() => setOpen((o) => !o)} data-testid="recovery-digest-toggle">
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          <FileClock className="w-4 h-4 text-cyan-400" /> Recovery digest
        </button>
        {d ? <span className="text-xs text-muted-foreground">last 7d · {d.fired} fired · {d.resolved} resolved{d.mttr_hours != null ? ` · MTTR ${d.mttr_hours}h` : ''}</span> : null}
        <div className="flex items-center gap-2 ml-auto">
          <span className="text-[11px] text-muted-foreground hidden sm:inline">Weekly recap</span>
          <Select value={cadence} onValueChange={changeCadence} disabled={busy === 'cadence'}>
            <SelectTrigger className="w-28 h-8" data-testid="recovery-digest-cadence"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="off">Off</SelectItem>
              <SelectItem value="weekly">Weekly</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" variant="ghost" onClick={sendTest} disabled={busy === 'test'} data-testid="recovery-digest-test">
            {busy === 'test' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Mail className="w-3.5 h-3.5 mr-1" /> Test to me</>}
          </Button>
          <Button size="sm" variant="outline" onClick={sendNow} disabled={busy === 'send'} data-testid="recovery-digest-send">
            {busy === 'send' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Send className="w-3.5 h-3.5 mr-1" /> Send now</>}
          </Button>
        </div>
      </div>
      {open && d ? (
        <div className="px-3 pb-3 space-y-3" data-testid="recovery-digest-body">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div className="rounded-lg border border-border/50 p-2.5"><div className="text-[10px] uppercase text-muted-foreground">Fired</div><div className="text-lg font-semibold">{d.fired}</div></div>
            <div className="rounded-lg border border-border/50 p-2.5"><div className="text-[10px] uppercase text-muted-foreground">Resolved</div><div className="text-lg font-semibold text-emerald-600 dark:text-emerald-300">{d.resolved}</div></div>
            <div className="rounded-lg border border-border/50 p-2.5"><div className="text-[10px] uppercase text-muted-foreground">MTTR</div><div className="text-lg font-semibold">{d.mttr_hours != null ? `${d.mttr_hours}h` : '—'}</div></div>
            <div className="rounded-lg border border-border/50 p-2.5"><div className="text-[10px] uppercase text-muted-foreground">Recovery verified</div><div className="text-lg font-semibold">{d.recovery_verified}{d.avg_recovery_hours != null ? <span className="text-xs text-muted-foreground"> · {d.avg_recovery_hours}h</span> : null}</div></div>
          </div>
          {d.sentinels?.length ? (
            <div className="space-y-1">
              {d.sentinels.map((s, i) => (
                <div key={i} className="flex items-center justify-between gap-2 text-xs py-1 border-b border-border/40 last:border-0" data-testid={`recovery-digest-sentinel-${i}`}>
                  <span className="font-medium truncate">{s.sentinel_name}</span>
                  <span className="text-muted-foreground shrink-0 text-right">
                    {s.fired} fired · {s.resolved} resolved{s.avg_resolution_hours != null ? ` · avg ${s.avg_resolution_hours}h` : ''}
                    {s.resolver_names?.length ? ` · ${s.resolver_names.join(', ')}` : ''}
                  </span>
                </div>
              ))}
            </div>
          ) : <div className="text-xs text-muted-foreground">No incidents in the last 7 days — a quiet week.</div>}
          <div className="pt-2 border-t border-border/40">
            <div className="text-[10px] uppercase text-muted-foreground mb-1.5">
              Recipients {recipients.length ? `· ${recipients.length} chosen` : '· all eligible stewards'}
            </div>
            <div className="flex flex-wrap gap-1.5" data-testid="recovery-digest-recipients">
              {candidates.map((c) => {
                const on = recipients.includes(c.user_id);
                return (
                  <button key={c.user_id} onClick={() => toggleRecipient(c.user_id)} disabled={busy === 'recips'}
                    className={`text-[11px] rounded-full border px-2 py-0.5 ${on ? 'border-cyan-500/50 bg-cyan-500/10 text-cyan-600 dark:text-cyan-300' : 'border-border/50 text-muted-foreground'}`}
                    data-testid={`recovery-digest-recipient-${c.user_id}`}>
                    {c.name || c.email || c.user_id}
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] text-muted-foreground mt-1.5">Pick specific people, or leave empty so all stewards, admins &amp; managers receive it.</p>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function IncidentsPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [incidents, setIncidents] = useState([]);
  const [counts, setCounts] = useState({});
  const [status, setStatus] = useState('open');
  const [severity, setSeverity] = useState('all');
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getSentinelIncidents(status === 'all' ? '' : status, severity === 'all' ? '' : severity,
        params.get('sentinel_id') || '');
      setIncidents(res.incidents || []);
      setCounts(res.counts || {});
      setSelected((prev) => {
        if (!prev) return (res.incidents || [])[0] || null;
        return (res.incidents || []).find((i) => i.incident_id === prev.incident_id) || (res.incidents || [])[0] || null;
      });
    } catch (e) {
      toast.error(e.message || 'Failed to load incidents');
    } finally {
      setLoading(false);
    }
  }, [status, severity, params]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-5" data-testid="incidents-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <AlertOctagon className="w-6 h-6 text-rose-500" /> Incidents
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Managed incidents raised by Sentinels — acknowledge, assign, investigate, resolve and verify recovery.</p>
        </div>
        <div className="flex items-center gap-2">
          <ProcessHelp
            testId="incidents-help"
            title="Incidents & Recovery Digest"
            section="incidents"
            intro="The inbox of managed incidents raised by Sentinels — triage them here and track recovery with the weekly digest."
            steps={[
              'Filter by status or severity, then open an incident to read its evidence.',
              'Move it through the lifecycle: Acknowledge → Assign → Resolve → Verify recovery (every step is audited).',
              'Use "Investigate why" to hand an incident to root-cause analysis.',
              'Recovery Digest (panel below) gives a weekly recap — toggle Off / Weekly and pick recipients.',
              '"Test to me" sends you a one-off preview; "Send now" delivers the digest immediately.',
            ]}
          />
          <Button variant="outline" size="sm" onClick={() => navigate('/admin/sentinels')} data-testid="incidents-sentinels-link">
            <Radar className="w-4 h-4 mr-1.5" /> Sentinels
          </Button>
          <Button variant="outline" size="sm" onClick={load} data-testid="incidents-refresh"><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </div>

      <RecoveryDigestPanel />

      <div className="flex items-center gap-2 flex-wrap">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-48" data-testid="incidents-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="open">Open ({(counts.open || 0) + (counts.acknowledged || 0) + (counts.assigned || 0)})</SelectItem>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="acknowledged">Acknowledged</SelectItem>
            <SelectItem value="assigned">Assigned</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
            <SelectItem value="recovery_verified">Recovery verified</SelectItem>
          </SelectContent>
        </Select>
        <Select value={severity} onValueChange={setSeverity}>
          <SelectTrigger className="w-40" data-testid="incidents-severity-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All severities</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : incidents.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border/60 py-16 text-center" data-testid="incidents-empty">
          <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-500/60 mb-3" />
          <p className="text-sm text-muted-foreground">No incidents match this filter. Your Sentinels are quiet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
          <div className="lg:col-span-3 rounded-xl border border-border/60 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-muted-foreground">
                <tr className="text-left">
                  <th className="px-3 py-2.5 font-medium">Severity</th>
                  <th className="px-3 py-2.5 font-medium">Incident</th>
                  <th className="px-3 py-2.5 font-medium">Status</th>
                  <th className="px-3 py-2.5 font-medium">Opened</th>
                </tr>
              </thead>
              <tbody>
                {incidents.map((i) => (
                  <tr key={i.incident_id}
                    className={`border-t border-border/50 cursor-pointer hover:bg-muted/20 ${selected?.incident_id === i.incident_id ? 'bg-muted/30' : ''}`}
                    onClick={() => setSelected(i)} data-testid={`incident-row-${i.incident_id}`}>
                    <td className="px-3 py-3"><Badge className={SEV_STYLE[i.severity]}>{i.severity}</Badge></td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium line-clamp-1">{i.headline}</span>
                        {i.has_story && (
                          <Badge className="text-[9px] bg-violet-500/15 text-violet-500 border-violet-500/25 gap-1 shrink-0" data-testid={`incident-story-badge-${i.incident_id}`} title="Linked to an investigation, recommendation or approval">
                            <GitBranch className="w-2.5 h-2.5" /> In a story
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">{i.sentinel_name} · {i.metric_label || i.metric}</div>
                    </td>
                    <td className="px-3 py-3"><Badge className={INCIDENT_STATUS_STYLE[i.status]}>{INCIDENT_STATUS_LABEL[i.status] || i.status}</Badge></td>
                    <td className="px-3 py-3 text-xs text-muted-foreground">{timeAgo(i.opened_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="lg:col-span-2">
            {selected ? <IncidentDetail incident={selected} onChanged={load} /> : (
              <div className="rounded-xl border border-dashed border-border/60 py-16 text-center text-sm text-muted-foreground">
                Select an incident to view its evidence.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
